import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDownTrayIcon,
  DocumentCheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  DescriptionList,
  EmptyState,
  PageHeader,
  SkeletonRows,
  TextAreaField,
  TextField,
  useToast,
  type Column,
} from '@csa/ui';
import { api, ApiError } from '../../lib/api.js';
import { useTimeZone } from '../../lib/TimeZoneProvider.js';

/**
 * Evidence packs.
 *
 * What an auditor is actually handed: one file per section, a manifest
 * listing each file's SHA-256, and a detached KMS signature over the
 * manifest. They verify the signature with the public key, check each
 * digest, and from then on hold something they can reason about without
 * trusting this application or having access to the AWS account.
 *
 * The page exists mainly to make two things impossible to miss: that
 * producing a pack is itself recorded, and that a pack whose chain
 * verification failed is marked as such. A failed pack is still produced --
 * an auditor must be able to see the break -- but it must never be handed
 * over as though it were clean.
 */

interface Pack {
  readonly id: string;
  readonly covers_from: string;
  readonly covers_to: string;
  readonly purpose: string;
  readonly created_at: string;
  readonly s3_prefix: string;
  readonly chain_verified: boolean;
  readonly section_count: number;
  readonly manifest_sha256: string;
  readonly requested_by_upn: string | null;
}

interface Manifest {
  readonly packId: string;
  readonly coverage: { from: string; to: string };
  readonly sections: readonly {
    readonly name: string;
    readonly key: string;
    readonly format: string;
    readonly recordCount: number;
    readonly sha256: string;
    readonly bytes: number;
    readonly description: string;
  }[];
  readonly chain: {
    readonly verified: boolean;
    readonly eventCount: number;
    readonly headHash: string | null;
    readonly failures: readonly { kind: string; seq: string }[];
    readonly gaps: readonly string[];
  };
  readonly checkpoints: readonly {
    readonly coversDate: string;
    readonly s3Key: string;
    readonly objectLockUntil: string | null;
  }[];
  readonly verification: {
    readonly signingKeyArn: string;
    readonly signingAlgorithm: string;
    readonly instructions: string;
  };
}

const SECTION_FILES = [
  'manifest.json',
  'manifest.sig',
  'audit-events.ndjson.gz',
  'jobs.ndjson.gz',
  'approvals.ndjson.gz',
  'entitlements-snapshot.json.gz',
  'configuration.json.gz',
];

export function EvidencePage(): ReactElement {
  const { toast } = useToast();
  const tz = useTimeZone();
  const queryClient = useQueryClient();

  const [from, setFrom] = useState(() => monthsAgo(3));
  const [to, setTo] = useState(() => today());
  const [purpose, setPurpose] = useState('');
  const [manifest, setManifest] = useState<Manifest | null>(null);

  const packs = useQuery({
    queryKey: ['evidence-packs'],
    queryFn: () => api.get<{ packs: Pack[] }>('/api/admin/evidence'),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<{ manifest: Manifest }>('/api/admin/evidence', { from, to, purpose }),

    onSuccess: ({ manifest: produced }) => {
      setManifest(produced);
      setPurpose('');
      void queryClient.invalidateQueries({ queryKey: ['evidence-packs'] });

      toast(
        produced.chain.verified
          ? {
              tone: 'success',
              title: 'Evidence pack signed',
              message: `${produced.chain.eventCount.toLocaleString()} events, chain verified.`,
            }
          : {
              tone: 'danger',
              title: 'Pack produced, but the chain did not verify',
              message: 'Do not hand this over until the chain break has been investigated.',
              // Left on screen. A control failure that fades away after five
              // seconds is a control failure nobody acts on.
              durationMs: 0,
            },
      );
    },

    onError: (error: unknown) => {
      toast({
        tone: 'danger',
        title: 'Could not produce the pack',
        message: error instanceof ApiError ? error.message : String(error),
      });
    },
  });

  const columns: Column<Pack>[] = [
    {
      key: 'created_at',
      header: `Produced (${tz.timeZone})`,
      render: (pack) => (
        <span className="whitespace-nowrap text-xs" title={tz.titleFor(pack.created_at)}>
          {tz.formatBare(pack.created_at).value}
        </span>
      ),
    },
    {
      key: 'coverage',
      header: 'Covers',
      render: (pack) => (
        <span className="whitespace-nowrap font-mono text-xs">
          {pack.covers_from.slice(0, 10)} → {pack.covers_to.slice(0, 10)}
        </span>
      ),
    },
    { key: 'requested_by_upn', header: 'Requested by', render: (p) => p.requested_by_upn ?? '—' },
    { key: 'purpose', header: 'Purpose', render: (p) => p.purpose },
    {
      key: 'chain_verified',
      header: 'Chain',
      render: (pack) =>
        pack.chain_verified ? (
          <Badge tone="success">verified</Badge>
        ) : (
          <Badge tone="danger">FAILED</Badge>
        ),
    },
    {
      key: 'download',
      header: '',
      render: (pack) => <PackDownloads packId={pack.id} />,
    },
  ];

  const spanDays =
    (new Date(`${to}T00:00:00Z`).getTime() - new Date(`${from}T00:00:00Z`).getTime()) / 86_400_000;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <PageHeader
        title="Evidence packs"
        description="Signed, immutable extracts for an external auditor. Producing one is recorded in the audit trail."
      />

      <Card className="flex flex-col gap-3 p-3">
        <div className="flex flex-wrap items-end gap-3">
          {/*
            UTC dates, deliberately, and labelled as such -- unlike the audit
            search filter, which is drawn in the reader's zone.
            
            The difference is what the field is for. A search boundary is a
            convenience for the person looking, so it should mean what they
            expect where they are sitting. A pack's coverage period is part of
            the artefact: it appears in the manifest, it is what the auditor
            cites, and two people requesting "March" must get the same bytes.
            A period that shifted with the requester's zone would produce two
            packs that both claim to cover March and do not agree at the
            edges.
          */}
          <TextField
            label="From (UTC)"
            type="date"
            containerClassName="w-44"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          />
          <TextField
            label="To (UTC)"
            type="date"
            containerClassName="w-44"
            value={to}
            onChange={(event) => setTo(event.target.value)}
          />
          <p className="pb-2 text-xs text-subtle">
            {spanDays >= 0 ? `${Math.round(spanDays) + 1} days` : 'invalid range'}
          </p>
        </div>

        <TextAreaField
          label="Why is this pack being produced?"
          hint="Recorded in the manifest and the audit trail. “Because an auditor asked for Q3” is a different answer from “because I wanted a copy of the ledger”."
          className="h-20"
          value={purpose}
          onChange={(event) => setPurpose(event.target.value)}
          placeholder="FY26 Q1 SOX walkthrough — requested by external audit, engagement ABC-123"
        />

        <div className="flex items-center gap-3">
          <Button
            onClick={() => create.mutate()}
            loading={create.isPending}
            disabled={purpose.trim().length < 10 || spanDays < 0 || spanDays > 91}
            leadingIcon={<DocumentCheckIcon className="size-4" aria-hidden />}
          >
            Produce and sign
          </Button>

          {/* Stated plainly rather than discovered as a validation error
              after somebody has typed a purpose and picked a year. */}
          <p className="text-xs text-subtle">
            Up to 92 days per pack. Producing one reads the whole range and takes a minute or two.
          </p>
        </div>

        {spanDays > 91 ? (
          <Alert tone="warning">
            That range is wider than 92 days. Produce several packs, or run the scheduled export
            task for a full year.
          </Alert>
        ) : null}
      </Card>

      {manifest ? <ManifestView manifest={manifest} /> : null}

      <section className="flex min-h-0 flex-col gap-2">
        <h2 className="text-sm font-medium">Packs already issued</h2>

        {packs.isPending ? (
          <SkeletonRows rows={4} />
        ) : (packs.data?.packs.length ?? 0) === 0 ? (
          <EmptyState
            title="No packs have been issued"
            description="Produce one above when an auditor asks for a period."
          />
        ) : (
          <DataTable
            columns={columns}
            rows={packs.data!.packs}
            rowKey={(pack) => pack.id}
            caption="Issued evidence packs, newest first"
          />
        )}
      </section>
    </div>
  );
}

function ManifestView({ manifest }: { manifest: Manifest }): ReactElement {
  return (
    <Card tone={manifest.chain.verified ? 'success' : 'danger'} className="flex flex-col gap-3 p-3">
      {!manifest.chain.verified ? (
        <Alert
          tone="danger"
          title="The hash chain did not verify over this range"
          icon={<ExclamationTriangleIcon className="size-5" aria-hidden />}
        >
          <p>
            {manifest.chain.failures.length} event
            {manifest.chain.failures.length === 1 ? '' : 's'} do not match their recorded hash
            {manifest.chain.gaps.length > 0
              ? `, and ${manifest.chain.gaps.length} sequence number(s) are missing`
              : ''}
            . The pack has still been produced and signed, because an auditor must be able to see
            the break — but it says so, and it must not be handed over as clean. Follow the
            chain-break runbook.
          </p>
        </Alert>
      ) : null}

      <DescriptionList
        items={[
          { label: 'Pack', value: <code className="font-mono text-xs">{manifest.packId}</code> },
          {
            label: 'Coverage',
            value: `${manifest.coverage.from} to ${manifest.coverage.to}`,
          },
          { label: 'Events', value: manifest.chain.eventCount.toLocaleString() },
          {
            label: 'Chain head',
            value: manifest.chain.headHash ? (
              <code className="break-all font-mono text-xs">{manifest.chain.headHash}</code>
            ) : (
              '—'
            ),
          },
          {
            label: 'Signed with',
            value: (
              <code className="break-all font-mono text-xs">
                {manifest.verification.signingKeyArn} ({manifest.verification.signingAlgorithm})
              </code>
            ),
          },
          {
            label: 'Daily checkpoints in range',
            value: `${manifest.checkpoints.length} signed, under Object Lock`,
          },
        ]}
      />

      <section>
        <h3 className="mb-1.5 text-xs font-medium text-subtle">Sections</h3>
        <ul className="flex flex-col gap-1.5 text-sm">
          {manifest.sections.map((section) => (
            <li key={section.key} className="rounded-md border border-line p-2">
              <div className="flex flex-wrap items-center gap-2">
                <code className="font-mono text-xs">{section.name}</code>
                <Badge tone="neutral">{section.recordCount.toLocaleString()} records</Badge>
                <span className="text-xs text-muted">{formatBytes(section.bytes)}</span>
              </div>
              <p className="mt-1 text-xs text-muted">{section.description}</p>
              {/* The digest is shown in full because it is what the auditor
                  runs sha256sum against. Truncating it would make the one
                  verification step they must perform impossible from this
                  screen. */}
              <p className="mt-1 break-all font-mono text-[0.7rem] text-subtle">
                sha256 {section.sha256}
              </p>
            </li>
          ))}
        </ul>
      </section>

      <Alert tone="info">{manifest.verification.instructions}</Alert>
    </Card>
  );
}

/**
 * Per-file download links.
 *
 * One link per section rather than a single archive, because the manifest
 * names each file with its own digest -- an auditor verifies file by file,
 * and a zip would have to be unpacked before any digest could be checked.
 *
 * Each URL is fetched on click and used immediately, never held in state or
 * rendered as an href. A presigned URL is a bearer credential: anything that
 * puts it in the DOM puts it in a screenshot and a browser history entry.
 */
function PackDownloads({ packId }: { packId: string }): ReactElement {
  const { toast } = useToast();
  const [busy, setBusy] = useState<string | null>(null);

  const download = async (section: string): Promise<void> => {
    setBusy(section);
    try {
      const { url } = await api.post<{ url: string }>(`/api/admin/evidence/${packId}/download`, {
        section,
      });
      window.location.assign(url);
    } catch (error) {
      toast({
        tone: 'danger',
        title: 'Could not get a download link',
        message: error instanceof ApiError ? error.message : String(error),
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-wrap gap-1">
      {SECTION_FILES.map((section) => (
        <Button
          key={section}
          size="xs"
          variant="ghost"
          loading={busy === section}
          leadingIcon={<ArrowDownTrayIcon className="size-3.5" aria-hidden />}
          onClick={() => void download(section)}
        >
          {section
            .replace(/\.(ndjson|json)\.gz$/, '')
            .replace('.json', '')
            .replace('.sig', ' sig')}
        </Button>
      ))}
    </div>
  );
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function monthsAgo(months: number): string {
  const date = new Date();
  date.setUTCMonth(date.getUTCMonth() - months);
  return date.toISOString().slice(0, 10);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
